"use client";

import { toast } from "sonner";
import React, { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useCart } from "@/src/contexts/CartContext";
import { useTenant } from "@/src/contexts/TenantContext";
import { getProductDetails } from "@/src/services/product.service";
import type { ProductDetailData, ProductReview, RelevantProduct } from "@/src/types/product";
import { CountdownTimer } from "@/src/app/shop/components/CountdownTimer";
import { SafeImage } from "@/src/components/SafeImage";
import { ProductDescription } from "./components/ProductDescription";
import { FreeDeliveryNudge } from "@/src/app/shop/components/FreeDeliveryNudge";
import { ProductHighlights } from "@/src/app/shop/components/ProductHighlights";
import { DeliveryEstimate } from "@/src/app/shop/components/DeliveryEstimate";
import { VariantSelector } from "./components/VariantSelector";
import { VariantAxesSelector, type VariantAxis, type V3Variant } from "./components/VariantAxesSelector";
import { isSoldOut } from "@/src/utils/products/stock";

/* ── Star row ── */
function StarRow({ rating, size = 13 }: { rating: number; size?: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => {
        const filled = rating >= s;
        const half = !filled && rating >= s - 0.5;
        return (
          <svg key={s} width={size} height={size} viewBox="0 0 24 24">
            <defs>
              <linearGradient id={`sg-${s}`}>
                <stop offset="50%" stopColor="#E8A020" />
                <stop offset="50%" stopColor="transparent" />
              </linearGradient>
            </defs>
            <polygon
              points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
              fill={filled ? "#E8A020" : half ? `url(#sg-${s})` : "none"}
              stroke="#E8A020" strokeWidth="1.5"
            />
          </svg>
        );
      })}
    </div>
  );
}

interface Props {
  productId: string;
  outletId: string;
  prefix: string;
  minCartValue?: number | null;
  /** Seller's home-page key highlights, shown under Add to cart. */
  highlightBadges?: { title: string; sub: string; icon?: string }[];
  /**
   * Product seeded from the landing payload by the server component, so the
   * name/price/description/images are in the server HTML rather than appearing
   * only once the fetch below resolves. The fetch still runs and overwrites this
   * with the richer payload (brand, reviews, relevantProducts).
   */
  initialProduct?: ProductDetailData | null;
}

export default function ProductDetailClient({
  productId,
  outletId,
  prefix,
  minCartValue,
  highlightBadges = [],
  initialProduct = null,
}: Props) {
  const router = useRouter();
  const { addItem, getItemQuantity, updateQuantity, removeItem } = useCart();
  const { tenantId } = useTenant();

  const [product, setProduct] = useState<ProductDetailData | null>(initialProduct);
  const [relevantProducts, setRelevantProducts] = useState<RelevantProduct[]>([]);
  const [reviews, setReviews] = useState<ProductReview[]>([]);
  const [outletRating, setOutletRating] = useState(0);
  const [reviewCount, setReviewCount] = useState(0);
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  // Photo of the colour picked in the axes selector — available before the whole
  // combination (colour + size) resolves to a variant.
  const [axisImage, setAxisImage] = useState<string | null>(null);
  // Structured variant axes (Size / Color / Width / Age / anything) from the v3 product API.
  const [axes, setAxes] = useState<VariantAxis[]>([]);
  const [v3Variants, setV3Variants] = useState<V3Variant[]>([]);
  const [highlights, setHighlights] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const base = process.env.NEXT_PUBLIC_PRODUCT_SERVICE_URL;
        if (!base) return;
        const res = await fetch(`${base}/v3/products/${productId}`);
        if (!res.ok) return;
        const json = await res.json();
        const d = json?.data;
        if (!cancelled && d) {
          setAxes(Array.isArray(d.variantAxes) ? d.variantAxes : []);
          setV3Variants(Array.isArray(d.variants) ? d.variants : []);
          const hl = d.presentation?.content?.highlights;
          setHighlights(Array.isArray(hl) ? hl.filter(Boolean) : []);
          const tg = d.product?.metadata?.tags;
          setTags(Array.isArray(tg) ? tg.filter(Boolean) : []);
        }
      } catch {
        /* fall back to the flat variant selector */
      }
    })();
    return () => { cancelled = true; };
  }, [productId]);
  const [localQty, setLocalQty] = useState(1);
  const [descExpanded, setDescExpanded] = useState(false);
  // Seeded from the server → skip the skeleton entirely and paint real content
  // on the first frame; the fetch below refines it in the background.
  const [loading, setLoading] = useState(!initialProduct);
  const [error, setError] = useState("");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const thumbsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setLightboxOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getProductDetails(productId, outletId);
        if (res.success && res.data) {
          setProduct(res.data.product);
          setRelevantProducts(res.data.relevantProducts ?? []);
          setReviews(res.data.productReviews ?? []);
          setOutletRating(res.data.outletRating ?? 0);
          setReviewCount(res.data.outletReviewCount ?? 0);
        } else {
          setError("Product not found");
        }
      } catch {
        setError("Failed to load product");
      } finally {
        setLoading(false);
      }
    })();
  }, [productId, outletId]);

  /* ── Loading skeleton ── */
  if (loading) {
    return (
      <div className="mx-auto max-w-[1280px] px-6 py-10 animate-pulse">
        <div className="mb-6 h-4 w-64 rounded bg-[#EDE9E1]" />
        <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
          <div className="space-y-3">
            <div className="aspect-square rounded-2xl bg-[#EDE9E1]" />
            <div className="flex gap-2">
              {[1,2,3,4].map(i => <div key={i} className="h-16 w-16 rounded-xl bg-[#EDE9E1]" />)}
            </div>
          </div>
          <div className="space-y-5 pt-2">
            <div className="h-10 w-3/4 rounded-xl bg-[#EDE9E1]" />
            <div className="h-6 w-1/2 rounded-lg bg-[#EDE9E1]" />
            <div className="h-10 w-40 rounded-xl bg-[#EDE9E1]" />
            <div className="h-32 rounded-2xl bg-[#EDE9E1]" />
            <div className="h-12 rounded-2xl bg-[#EDE9E1]" />
            <div className="h-12 rounded-2xl bg-[#EDE9E1]" />
          </div>
        </div>
      </div>
    );
  }

  // Deliberately NOT `error || !product`: with a server-seeded product a failed
  // background refresh must not replace good rendered content with an error
  // screen. Googlebot indexes the post-JS DOM, so that flip would have made the
  // crawler see "Product not found" on a page whose HTML was correct.
  if (!product) {
    return (
      <div className="mx-auto max-w-[1280px] px-6 py-10">
        <Link href={`/shop?prefix=${prefix}`}
          className="mb-6 flex w-fit items-center gap-2 text-sm text-[#8A8A85] hover:text-[#1D1D1D]">
          ← Back to shop
        </Link>
        <p className="text-[#8A8A85]">{error || "Product not found"}</p>
      </div>
    );
  }

  // ── Variants ── selector only appears for 2+ variants; none pre-selected.
  const variants = product.variants ?? [];
  const hasVariants = variants.length >= 2;
  const selectedVariant = hasVariants
    ? variants.find((v) => v.variantId === selectedVariantId) ?? null
    : null;
  const mustSelectVariant = hasVariants && !selectedVariant;

  // ── Pricing: a selected variant overrides the base product. Offer pricing
  // applies to the base product only (variants carry no offer fields). ──
  const baseMrp = parseFloat(product.mrpPrice) || 0;
  const baseSelling = parseFloat(product.sellingPrice) || 0;
  const offerPrice = parseFloat(product.offerPrice || "0") || 0;
  const offerActive = !selectedVariant &&
    offerPrice > 0 &&
    !!product.offerEndTime &&
    // eslint-disable-next-line react-hooks/purity
    Date.now() <= new Date(product.offerEndTime).getTime();

  const variantSelling = selectedVariant ? parseFloat(selectedVariant.sellingPrice || "0") : 0;
  const variantMrp = selectedVariant ? parseFloat(selectedVariant.mrp || "0") : 0;
  const mrp = selectedVariant ? variantMrp || baseMrp : baseMrp;
  const selling = selectedVariant
    ? variantSelling || baseSelling
    : offerActive
      ? offerPrice
      : baseSelling;
  const discountPct = mrp > 0 && mrp > selling ? Math.round(((mrp - selling) / mrp) * 100) : 0;
  const savings = mrp > selling ? mrp - selling : 0;

  const stockQty = selectedVariant ? selectedVariant.stockQuantity ?? 0 : product.stockQuantity;
  const inStock = stockQty > 0;

  // Image source: the selected variant's own images when it has any, else the product's.
  // The CRM sets `thumbnail: uploadedImageUrls[0]` on save, so the thumbnail is
  // normally already the first entry of imageUrls — prepending it blindly shows
  // the first photo twice in the strip. Dedupe by URL, thumbnail first.
  const dedupe = (urls: (string | undefined)[]) => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const url of urls) {
      if (url && !seen.has(url)) {
        seen.add(url);
        out.push(url);
      }
    }
    return out;
  };
  const variantImages = selectedVariant
    ? dedupe([selectedVariant.thumbnail, ...(selectedVariant.imageUrls || [])])
    : [];
  const baseImages = variantImages.length > 0
    ? variantImages
    : dedupe([product.thumbnail, ...(product.imageUrls || [])]);
  // A colour pick promotes that colour's photo to the front of the gallery.
  const allImages = axisImage
    ? [axisImage, ...baseImages.filter((img) => img !== axisImage)]
    : baseImages;

  // CTAs require an in-stock selection; with variants present, one must be chosen.
  const canAct = inStock && !mustSelectVariant;
  const cartQty = getItemQuantity(productId, selectedVariant?.variantId);
  const showStepper = cartQty > 0 && !mustSelectVariant;

  const handleSelectVariant = (variantId: string) => {
    setSelectedVariantId(variantId);
    setSelectedImage(0);
  };

  // Show the colour's photo (now first in the gallery) as soon as it's picked.
  const handleAxisImage = (image: string | null) => {
    setAxisImage(image);
    setSelectedImage(0);
  };

  const handleAddToCart = () => {
    if (!tenantId) {
      console.warn("[ProductDetail] missing tenantId — cannot add to cart");
      return;
    }
    if (!canAct) return; // out of stock, or a variant still needs to be chosen
    addItem({
      productId,
      outletId,
      tenantId,
      variantId: selectedVariant?.variantId,
      name: selectedVariant ? `${product.name} (${selectedVariant.label})` : product.name,
      price: selling,
      mrp: mrp || undefined,
      quantity: localQty,
      image: allImages[0] || product.thumbnail || undefined,
    }).catch((err: unknown) => toast.error((err as Error)?.message || "Couldn't add to cart"));
  };

  const handleBuyNow = () => {
    if (!canAct) return;
    handleAddToCart();
    router.push(`/shop/checkout?prefix=${prefix}`);
  };

  const handleQtyChange = (id: string, qty: number, variantId?: string) => {
    if (qty <= 0) {
      removeItem(id, variantId).catch((err) => console.warn("[ProductDetail] removeItem failed:", err?.message));
    } else {
      updateQuantity(id, qty, variantId).catch((err) =>
        console.warn("[ProductDetail] updateQuantity failed:", err?.message)
      );
    }
  };

  const goToImage = (dir: "prev" | "next") => {
    const next =
      dir === "next"
        ? Math.min(allImages.length - 1, selectedImage + 1)
        : Math.max(0, selectedImage - 1);
    if (next === selectedImage) return;
    setSelectedImage(next);
    // Keep the newly selected thumbnail visible in the strip.
    (thumbsRef.current?.children[next] as HTMLElement | undefined)?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  };

  /* Number of shoppers — derived from review count for social proof feel */
  const socialProofNum = Math.max(reviewCount * 7 + 24, 12);

  return (
    <div className="min-h-screen text-[#1D1D1D]" style={{ background: "var(--rev-white)" }}>
       {/* Breadcrumb */}
      <div className="border-b border-[#F0EDE6]">
        <div className="mx-auto flex max-w-[1280px] items-center gap-1.5 px-6 py-3 text-[12px] text-[#8A8A85]">
          <Link href={`/shop?prefix=${prefix}`} className="hover:text-[#1D1D1D] transition cursor-pointer">
            Home
          </Link>
          <span>/</span>
          <span>
            Product
          </span>
          <span>/</span>
          <span className="text-[#1D1D1D] font-medium line-clamp-1">{product.name}</span>
        </div>
      </div>
      {/* ── Main two-column layout ── */}
      <div className="mx-auto max-w-[1280px] px-6 py-8 md:py-12">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-[1fr_1fr] lg:grid-cols-[55%_45%]">

          {/* ════ LEFT: image gallery ════ */}
          <div className="md:sticky md:top-20 md:self-start">
            {/* Main image */}
            <div
              className="relative overflow-hidden rounded-2xl bg-[#FAF8F4]"
              style={{ aspectRatio: "1/1", cursor: allImages.length > 0 ? "zoom-in" : "default" }}
              onClick={() => { if (allImages.length > 0) setLightboxOpen(true); }}
            >
              {allImages.length > 0 ? (
                <SafeImage
                  src={allImages[selectedImage]}
                  alt={product.name}
                  className="h-full w-full object-cover transition-opacity duration-200"
                />
              ) : (
                <div className="flex h-full items-center justify-center">
                  <span className="text-7xl font-black text-[#1D1D1D]/10">
                    {product.name.slice(0, 2).toUpperCase()}
                  </span>
                </div>
              )}

              {discountPct > 0 && (
                <div className="absolute right-3 top-3 rounded-lg px-2.5 py-1 text-[11px] font-bold text-white"
                  style={{ background: "#F26250" }}>
                  {discountPct}% OFF
                </div>
              )}
            </div>

            {/* Thumbnail strip */}
            {allImages.length > 1 && (
              <div className="relative mt-3 flex items-center gap-2">
                {allImages.length > 4 && (
                  <button onClick={() => goToImage("prev")} disabled={selectedImage === 0} aria-label="Previous image"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#E8E3DA] bg-white shadow-sm transition hover:bg-[#FAF8F4] disabled:cursor-not-allowed disabled:opacity-40">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1D1D1D" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6" /></svg>
                  </button>
                )}
                <div ref={thumbsRef} className="flex flex-1 gap-2 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
                  {allImages.map((img, i) => (
                    <button key={i} onClick={() => setSelectedImage(i)}
                      className="relative flex-shrink-0 overflow-hidden rounded-xl transition-all"
                      style={{
                        width: 72, height: 72,
                        border: `2px solid ${i === selectedImage ? "#F26250" : "transparent"}`,
                        background: "#FAF8F4",
                        opacity: i === selectedImage ? 1 : 0.55,
                      }}>
                      <SafeImage src={img} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
                {allImages.length > 4 && (
                  <button onClick={() => goToImage("next")} disabled={selectedImage === allImages.length - 1} aria-label="Next image"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#E8E3DA] bg-white shadow-sm transition hover:bg-[#FAF8F4] disabled:cursor-not-allowed disabled:opacity-40">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1D1D1D" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* ════ RIGHT: product info ════ */}
          <div className="flex flex-col gap-5">

            {/* Title */}
            <h1 className="text-[1.9rem] leading-tight tracking-[-0.01em] sm:text-[2.4rem]"
              style={{ color: "#1D1D1D", fontFamily: "var(--font-display)", fontWeight: 600 }}>
              {product.name}
            </h1>

            {/* Rating + brand */}
            <div className="flex flex-wrap items-center gap-4">
              {outletRating > 0 && (
                <div className="flex items-center gap-2">
                  <StarRow rating={outletRating} size={15} />
                  <span className="text-[13px] font-semibold text-[#1D1D1D]">{outletRating.toFixed(1)}</span>
                  <span className="text-[13px] text-[#8A8A85]">({reviewCount} {reviewCount === 1 ? "review" : "reviews"})</span>
                </div>
              )}
              {product.brand && product.brand !== "My Store" && (
                <span className="text-[13px] text-[#8A8A85]">
                  by <span className="font-semibold text-[#1D1D1D]">{product.brand}</span>
                </span>
              )}
            </div>

            {/* Stock urgency */}
            {inStock && stockQty <= 20 && !mustSelectVariant && (
              <div className="flex items-center gap-3 rounded-xl border border-[#E8E3DA] px-4 py-3"
                style={{ background: "#FDFAF6" }}>
                <span className="flex items-center gap-2 text-[13px] text-[#1D1D1D]">
                  <span className="inline-block h-2 w-2 rounded-full" style={{ background: "#F26250", animation: "blip 1s ease-in-out infinite" }} />
                  Only <strong>{stockQty}</strong> left at this price!
                </span>
              </div>
            )}

            {/* Pricing */}
            <div className="flex flex-wrap items-center gap-3">
              {mrp > selling && (
                <span className="text-[15px] text-[#8A8A85] line-through">₹{mrp.toLocaleString("en-IN")}</span>
              )}
              <span className="text-[2.1rem] leading-none tracking-[-0.01em]" style={{ color: "#1D1D1D", fontFamily: "var(--font-display)", fontWeight: 700 }}>
                ₹{selling.toLocaleString("en-IN")}
              </span>
              {savings > 0 && (
                <span className="rounded-lg px-3 py-1 text-[12px] font-bold text-white"
                  style={{ background: "#F26250" }}>
                  SAVE ₹{savings.toLocaleString("en-IN")}
                </span>
              )}
            </div>

            {/* Delivery band for this outlet. Carries the distance here — the
                detail page has the room, and the number is what makes the day
                claim credible. Silent when we cannot locate the buyer. */}
            <DeliveryEstimate outletId={outletId} showDistance />

            {/* Countdown timer — show whenever offer is active */}
            {offerActive && product.offerEndTime && (
              <CountdownTimer label="Offer ends in" endsAt={product.offerEndTime} />
            )}

            {/* Variant selector — structured axes (Color swatches + Size / Width / Age / anything)
                when the v3 API provides them, otherwise the flat label list. */}
            {hasVariants && axes.length > 0 && v3Variants.length > 0 ? (
              <VariantAxesSelector
                axes={axes}
                variants={v3Variants}
                selectedVariantId={selectedVariantId}
                onSelect={handleSelectVariant}
                onImageChange={handleAxisImage}
              />
            ) : hasVariants ? (
              <VariantSelector
                variants={variants}
                selectedVariantId={selectedVariantId}
                onSelect={handleSelectVariant}
              />
            ) : null}

            {/* Key highlights — AI-generated */}
            {highlights.length > 0 && (
              <div className="flex flex-col gap-2.5 rounded-2xl border border-[#F0EDE6] bg-[#FBFAF7] px-5 py-4">
                <p className="text-[12px] font-bold uppercase tracking-wider text-[#8A8A85]">Key highlights</p>
                <ul className="flex flex-col gap-2">
                  {highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-[13.5px] leading-snug text-[#3A3A40]">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="mt-0.5 shrink-0">
                        <circle cx="12" cy="12" r="10" fill="#FEF0EC" />
                        <path d="M8 12.5l2.5 2.5L16 9.5" stroke="#F26250" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tags — AI-generated discovery tags */}
            {tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                {tags.map((t, i) => (
                  <span
                    key={i}
                    className="rounded-full border border-[#EDE9E1] bg-[#F7F5F0] px-3 py-1 text-[12px] font-semibold text-[#8A7F70]"
                  >
                    #{String(t).replace(/^#/, "")}
                  </span>
                ))}
              </div>
            )}

            {/* Description */}
            {product.description && (
              <ProductDescription
                description={product.description}
                expanded={descExpanded}
                onToggle={() => setDescExpanded(v => !v)}
              />
            )}

            {/* Best value note */}
            {discountPct > 0 && (
              <span
                className="inline-flex items-center gap-1.5 self-start rounded-full px-3 py-1.5 text-[11.5px] font-bold"
                style={{ background: "#FEF0EC", color: "#D94A39" }}
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: "#F26250" }} />
                Best value deal · Limited time only
              </span>
            )}

            {/* Combo items */}
            {product.isCombo && product.comboItems && product.comboItems.length > 0 && (
              <div className="rounded-2xl border border-[#E8E3DA] p-4" style={{ background: "#FDFAF6" }}>
                <p className="mb-3 text-[12px] font-bold uppercase tracking-wider text-[#8A8A85]">Included in bundle</p>
                <div className="space-y-2">
                  {product.comboItems.map((item) => (
                    <div key={item.productSku} className="flex items-center gap-3">
                      {item.thumbnail && (
                        <SafeImage src={item.thumbnail} alt={item.productName || ""} className="h-10 w-10 rounded-lg object-cover" />
                      )}
                      <div className="flex-1">
                        <p className="text-[13px] font-semibold">{item.productName || item.productSku}</p>
                        <p className="text-[11px] text-[#8A8A85]">Qty: {item.quantity}</p>
                      </div>
                      <p className="text-[13px] font-bold" style={{ color: "#F26250" }}>₹{item.sellingPrice}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Out of stock */}
            {!inStock && (
              <div className="rounded-xl border border-[#FECDCA] bg-[#FFF5F4] px-4 py-3 text-[13px] font-semibold text-[#F26250]">
                Currently out of stock
              </div>
            )}

            {/* Quantity + CTA */}
            {!showStepper ? (
              <>
                {mustSelectVariant && (
                  <p className="text-[13px] font-semibold" style={{ color: "#D94A39" }}>
                    Please select an option to continue
                  </p>
                )}
                <button onClick={handleAddToCart} disabled={!canAct}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 text-[15px] font-bold transition disabled:opacity-40"
                  style={{ height: 52, borderColor: "#F26250", color: "#F26250", background: "#FEF6F4" }}
                  onMouseEnter={e => { if (canAct) e.currentTarget.style.background = "#FBE4DF"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#FEF6F4"; }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                  </svg>
                  Add to cart
                </button>

                <button onClick={handleBuyNow} disabled={!canAct}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl text-[15px] font-bold text-white transition disabled:opacity-40"
                  style={{ height: 52, background: "#F26250", boxShadow: "0 6px 18px rgba(242, 98, 80,0.28)" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#D94A39"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#F26250"; }}>
                  Buy it now
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            ) : (
              /* Already in cart */
              <>
                {/* Qty stepper — full-width, replaces Add to cart */}
                <div className="flex w-full items-center overflow-hidden rounded-2xl border-2 border-[#F26250]"
                  style={{ height: 52, background: "#FEF6F4" }}>
                  <button
                    onClick={() => handleQtyChange(productId, cartQty - 1, selectedVariant?.variantId)}
                    className="flex h-full flex-1 items-center justify-center text-xl font-bold transition hover:bg-[#FBE4DF]"
                    style={{ color: "#F26250" }}>
                    −
                  </button>
                  <span className="flex min-w-[52px] items-center justify-center gap-1.5 text-[15px] font-bold"
                    style={{ color: "#1D1D1D" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#F26250" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                    </svg>
                    {cartQty}
                  </span>
                  <button
                    onClick={() => handleQtyChange(productId, cartQty + 1, selectedVariant?.variantId)}
                    className="flex h-full flex-1 items-center justify-center text-xl font-bold transition hover:bg-[#FBE4DF]"
                    style={{ color: "#F26250" }}>
                    +
                  </button>
                </div>

                <Link href={`/shop/checkout?prefix=${prefix}`}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl text-[15px] font-bold text-white transition"
                  style={{ height: 52, background: "#F26250", boxShadow: "0 6px 18px rgba(242, 98, 80,0.28)" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#D94A39"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "#F26250"; }}>
                  Proceed to Checkout
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M13 5l7 7-7 7" />
                  </svg>
                </Link>
              </>
            )}

            {/* Free-delivery nudge — real cart-based */}
            <FreeDeliveryNudge threshold={minCartValue ?? null} />

            <ProductHighlights badges={highlightBadges} minCartValue={minCartValue} />
          </div>
        </div>

        {/* ── Related products ── */}
        {relevantProducts.length > 0 && (
          <div className="mt-16 border-t border-[#F0EDE6] pt-12">
            <h2 className="mb-6 text-[1.6rem] text-[#1D1D1D]" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>You may also like</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
              {relevantProducts.map((rp) => {
                const rpMrp = parseFloat(rp.mrpPrice) || 0;
                const rpSelling = parseFloat(rp.sellingPrice) || 0;
                const rpDiscount = rpMrp > rpSelling && rpMrp > 0 ? Math.round(((rpMrp - rpSelling) / rpMrp) * 100) : 0;
                const rpCartQty = getItemQuantity(rp.id);
                const rpSoldOut = isSoldOut(rp.stockQuantity);

                return (
                  <div key={rp.id}
                    className="group relative cursor-pointer overflow-hidden rounded-2xl border border-[#F0EDE6] bg-white transition hover:border-[#E8E3DA] hover:shadow-md">
                    {/* Stretched link — see AllProducts for why this is an overlay. */}
                    <Link href={`/shop/product/${rp.id}?prefix=${prefix}&outletId=${outletId}`}
                      aria-label={rp.name} className="absolute inset-0 z-[1]" />
                    {/* Image */}
                    <div className="relative overflow-hidden bg-[#FAF8F4]" style={{ aspectRatio: "1/1" }}>
                      {rp.thumbnail ? (
                        <SafeImage src={rp.thumbnail} alt={rp.name}
                          className="h-full w-full object-cover transition duration-300 group-hover:scale-105" />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <span className="text-3xl font-black text-[#1D1D1D]/10">{rp.name.slice(0, 2).toUpperCase()}</span>
                        </div>
                      )}
                      {rpDiscount > 0 && (
                        <div className="absolute left-2 top-2 rounded-md px-2 py-0.5 text-[10px] font-bold text-white"
                          style={{ background: "#F26250" }}>
                          {rpDiscount}% OFF
                        </div>
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-3">
                      <p className="line-clamp-2 text-[12.5px] font-semibold leading-snug text-[#1D1D1D]">{rp.name}</p>
                      {rp.units && <p className="mt-0.5 text-[11px] text-[#8A8A85]">{rp.units}</p>}
                      {/* Related products ship from the same outlet, so the same band applies. */}
                      <DeliveryEstimate outletId={outletId} className="mt-1" />
                      <div className="mt-2 flex items-center justify-between gap-1">
                        <div>
                          <span className="text-[13px] font-bold text-[#1D1D1D]">₹{rpSelling}</span>
                          {rpMrp > rpSelling && (
                            <span className="ml-1 text-[11px] text-[#B0ABA3] line-through">₹{rpMrp}</span>
                          )}
                        </div>
                        {rpSoldOut ? (
                          <span
                            aria-label="Out of stock"
                            className="relative z-[2] cursor-not-allowed rounded-lg border px-2 py-1 text-[10px] font-bold uppercase"
                            style={{ borderColor: "var(--border)", color: "var(--techno-black-50)", background: "var(--surface-2)" }}
                          >
                            Sold out
                          </span>
                        ) : rpCartQty > 0 ? (
                          <div onClick={(e) => e.stopPropagation()}
                            className="relative z-[2] flex h-9 w-[68px] overflow-hidden rounded-lg sm:h-7"
                            style={{ background: "#F26250" }}>
                            <button onClick={(e) => { e.stopPropagation(); handleQtyChange(rp.id, rpCartQty - 1); }}
                              className="flex flex-1 items-center justify-center text-xs text-white hover:bg-white/10">−</button>
                            <span className="flex min-w-[22px] items-center justify-center text-[11px] font-bold text-white">{rpCartQty}</span>
                            <button onClick={(e) => { e.stopPropagation(); handleQtyChange(rp.id, rpCartQty + 1); }}
                              className="flex flex-1 items-center justify-center text-xs text-white hover:bg-white/10">+</button>
                          </div>
                        ) : (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!tenantId) {
                                console.warn("[ProductDetail] missing tenantId for related product");
                                return;
                              }
                              addItem({
                                productId: rp.id,
                                outletId,
                                tenantId,
                                name: rp.name,
                                price: rpSelling,
                                mrp: rpMrp || undefined,
                                quantity: 1,
                                image: rp.thumbnail || undefined,
                              }).catch((err) =>
                                toast.error((err as Error)?.message || "Couldn't add to cart")
                              );
                            }}
                            className="relative z-[2] flex h-9 w-9 items-center justify-center rounded-lg border-2 text-base font-bold transition sm:h-7 sm:w-7"
                            style={{ borderColor: "#F26250", color: "#F26250" }}
                            onMouseEnter={e => { e.currentTarget.style.background = "#F26250"; e.currentTarget.style.color = "#fff"; }}
                            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#F26250"; }}>
                            +
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Brand Reviews */}
        {reviews.length > 0 && (
          <div className="mt-16 border-t border-[#F0EDE6] pt-12">
            <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h2 className="text-[1.6rem] text-[#1D1D1D]" style={{ fontFamily: "var(--font-display)", fontWeight: 600 }}>Brand Reviews</h2>
                <p className="mt-1 text-[13px] text-[#8A8A85]">
                  {outletRating > 0 ? `${outletRating.toFixed(1)} · ${reviewCount} ${reviewCount === 1 ? "review" : "reviews"}` : "What customers are saying"}
                </p>
              </div>
              {outletRating > 0 && (
                <div className="flex items-center gap-3 rounded-2xl border border-[#E8E3DA] bg-[#FAF8F4] px-5 py-3">
                  <span className="text-[2rem] font-black leading-none text-[#1D1D1D]">{outletRating.toFixed(1)}</span>
                  <div>
                    <StarRow rating={outletRating} size={15} />
                    <p className="mt-1 text-[11px] text-[#8A8A85]">{reviewCount} {reviewCount === 1 ? "review" : "reviews"}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {reviews.map((review) => (
                <div key={review.id} className="rounded-2xl border border-[#F0EDE6] bg-[#FAF8F4] p-5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[13px] font-bold text-white"
                        style={{ background: "#F26250" }}>
                        {review.username.slice(0, 1).toUpperCase()}
                      </div>
                      <p className="text-[14px] font-semibold text-[#1D1D1D]">{review.username}</p>
                    </div>
                    <StarRow rating={review.userRating} size={13} />
                  </div>
                  {review.reviewText && (
                    <p className="mt-3 text-[13px] leading-relaxed text-[#4A4A4A]">{review.reviewText}</p>
                  )}
                  <p className="mt-2 text-[11px] text-[#B0ABA3]">
                    {new Date(review.time).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Mobile sticky CTA ── */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-[#F0EDE6] bg-white/95 px-4 py-3 backdrop-blur-sm md:hidden">
        {!showStepper ? (
          <div className="flex gap-3">
            <button onClick={handleAddToCart} disabled={!canAct}
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-2xl border-2 text-[14px] font-bold transition disabled:opacity-40"
              style={{ borderColor: "#F26250", color: "#F26250", background: "#FEF6F4" }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              Add to cart
            </button>
            <button onClick={handleBuyNow} disabled={!canAct}
              className="flex h-12 flex-1 items-center justify-center rounded-2xl text-[14px] font-bold text-white transition disabled:opacity-40"
              style={{ background: "#F26250" }}>
              Buy it now
            </button>
          </div>
        ) : (
          <div className="flex gap-3">
            <div className="flex h-12 w-[120px] shrink-0 items-center overflow-hidden rounded-2xl border-2"
              style={{ borderColor: "#1D1D1D" }}>
              <button onClick={() => handleQtyChange(productId, cartQty - 1, selectedVariant?.variantId)}
                className="flex flex-1 items-center justify-center text-lg text-[#1D1D1D]">−</button>
              <span className="min-w-[32px] text-center text-[14px] font-bold text-[#1D1D1D]">{cartQty}</span>
              <button onClick={() => handleQtyChange(productId, cartQty + 1, selectedVariant?.variantId)}
                className="flex flex-1 items-center justify-center text-lg text-[#1D1D1D]">+</button>
            </div>
            <Link href={`/shop/checkout?prefix=${prefix}`}
              className="flex flex-1 h-12 items-center justify-center rounded-2xl text-[14px] font-bold text-white"
              style={{ background: "#F26250" }}>
              Checkout →
            </Link>
          </div>
        )}
      </div>

      {/* ── Lightbox ── */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{ background: "rgba(10,10,10,0.92)", backdropFilter: "blur(6px)" }}
          onClick={() => setLightboxOpen(false)}
        >
          {/* Close */}
          <button
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full text-white transition"
            style={{ background: "rgba(255,255,255,0.12)" }}
            onClick={() => setLightboxOpen(false)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>

          {/* Prev */}
          {allImages.length > 1 && selectedImage > 0 && (
            <button
              className="absolute left-4 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full text-white transition"
              style={{ background: "rgba(255,255,255,0.12)" }}
              onClick={(e) => { e.stopPropagation(); setSelectedImage(i => i - 1); }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="15 18 9 12 15 6" /></svg>
            </button>
          )}

          {/* Next */}
          {allImages.length > 1 && selectedImage < allImages.length - 1 && (
            <button
              className="absolute right-4 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full text-white transition"
              style={{ background: "rgba(255,255,255,0.12)" }}
              onClick={(e) => { e.stopPropagation(); setSelectedImage(i => i + 1); }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6" /></svg>
            </button>
          )}

          {/* Main image */}
          <SafeImage
            src={allImages[selectedImage]}
            alt={product?.name}
            className="max-h-[85vh] max-w-[90vw] rounded-2xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Thumbnail strip — centered, capped to the viewport and horizontally
              scrollable so every thumbnail stays reachable on a phone. */}
          {allImages.length > 1 && (
            <div className="absolute bottom-5 left-0 right-0 px-4" onClick={(e) => e.stopPropagation()}>
              <div className="mx-auto flex w-max max-w-full gap-2 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
                {allImages.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedImage(i)}
                    className="overflow-hidden rounded-xl transition-all"
                    style={{
                      width: 52, height: 52, flexShrink: 0,
                      border: `2px solid ${i === selectedImage ? "#F26250" : "rgba(255,255,255,0.25)"}`,
                      opacity: i === selectedImage ? 1 : 0.5,
                    }}
                  >
                    <SafeImage src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}